import { useState } from "react";
import { Switch } from "@/components/ui/switch";

type EventClass =
  | "incident.opened"
  | "incident.resolved"
  | "incident.escalated"
  | "deployment.completed"
  | "approval.requested"
  | "approval.granted"
  | "maintenance.started"
  | "topology.changed";

const EVENT_LABELS: Record<EventClass, string> = {
  "incident.opened":     "Incident Opened",
  "incident.resolved":   "Incident Resolved",
  "incident.escalated":  "Incident Escalated",
  "deployment.completed":"Deployment Completed",
  "approval.requested":  "Approval Requested",
  "approval.granted":    "Approval Granted",
  "maintenance.started": "Maintenance Started",
  "topology.changed":    "Topology Changed",
};

interface Connection {
  id: string;
  label: string;
  type: string;
}

interface RoutingRule {
  event_class: EventClass;
  connection_ids: string[];
}

interface RoutingPolicyEditorProps {
  connections: Connection[];
  rules: RoutingRule[];
  onSave: (rules: RoutingRule[]) => Promise<void>;
}

export function RoutingPolicyEditor({ connections, rules, onSave }: RoutingPolicyEditorProps) {
  const [localRules, setLocalRules] = useState<RoutingRule[]>(rules);
  const [saving, setSaving] = useState(false);

  const isEnabled = (eventClass: EventClass, connectionId: string): boolean => {
    const rule = localRules.find(r => r.event_class === eventClass);
    return rule?.connection_ids.includes(connectionId) ?? false;
  };

  const toggle = (eventClass: EventClass, connectionId: string) => {
    setLocalRules(prev => {
      const existing = prev.find(r => r.event_class === eventClass);
      if (!existing) {
        return [...prev, { event_class: eventClass, connection_ids: [connectionId] }];
      }
      const ids = existing.connection_ids.includes(connectionId)
        ? existing.connection_ids.filter(id => id !== connectionId)
        : [...existing.connection_ids, connectionId];
      return prev.map(r => r.event_class === eventClass ? { ...r, connection_ids: ids } : r);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(localRules); } finally { setSaving(false); }
  };

  const eventClasses = Object.keys(EVENT_LABELS) as EventClass[];

  if (connections.length === 0) {
    return (
      <div className="border-2 border-dashed border-foreground/20 p-6 text-center">
        <p className="text-[10px] font-black uppercase text-muted-foreground">No connections yet. Add a channel first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-[9px] font-bold uppercase">
          <thead>
            <tr className="border-b-2 border-foreground">
              <th className="text-left py-2 pr-6 font-black tracking-widest text-muted-foreground min-w-[200px]">Event Class</th>
              {connections.map(c => (
                <th key={c.id} className="text-center py-2 px-4 font-black tracking-wider min-w-[80px]">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {eventClasses.map((ec, i) => (
              <tr key={ec} className={`border-b border-foreground/10 ${i % 2 === 0 ? "bg-accent/5" : ""}`}>
                <td className="py-2.5 pr-6 font-black tracking-wider">{EVENT_LABELS[ec]}</td>
                {connections.map(conn => (
                  <td key={conn.id} className="py-2.5 px-4 text-center">
                    <Switch
                      checked={isEnabled(ec, conn.id)}
                      onCheckedChange={() => toggle(ec, conn.id)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="h-9 px-4 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary transition-all disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Policy"}
        </button>
      </div>
    </div>
  );
}
